import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { X, Calendar, Clock } from 'lucide-react';

const ScheduleRideModal = ({ isOpen, onClose, onSchedule }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  if (!isOpen) return null;

  // Generate next 7 days
  const getNextDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      days.push({
        date: date.toISOString().split('T')[0],
        label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      });
    }
    return days;
  };

  // Generate time slots (every 30 minutes)
  const getTimeSlots = () => {
    const slots = [];
    const now = new Date();
    const startHour = selectedDate === getNextDays()[0].date ? now.getHours() + 1 : 0;
    
    for (let hour = startHour; hour < 24; hour++) {
      for (let min of [0, 30]) {
        const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const handleSchedule = () => {
    if (selectedDate && selectedTime) {
      const scheduledDateTime = new Date(`${selectedDate}T${selectedTime}`);
      onSchedule(scheduledDateTime);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <Card className="relative w-full max-w-lg mx-4 mb-4 sm:mb-0 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <Calendar className="w-6 h-6 mr-2 text-green-600" />
            Schedule Ride
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Date Selection */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              Select Date
            </label>
            <div className="grid grid-cols-2 gap-2">
              {getNextDays().map((day) => (
                <button
                  key={day.date}
                  onClick={() => setSelectedDate(day.date)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    selectedDate === day.date
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="text-xs text-gray-500">
                    {day.date === getNextDays()[0].date ? 'Today' : day.date === getNextDays()[1].date ? 'Tomorrow' : new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div className="font-semibold text-gray-900">
                    {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Time Selection */}
          {selectedDate && (
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                Select Time
              </label>
              <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                {getTimeSlots().map((time) => (
                  <button
                    key={time}
                    onClick={() => setSelectedTime(time)}
                    className={`p-2 rounded-lg border-2 text-sm transition-all ${
                      selectedTime === time
                        ? 'border-green-500 bg-green-50 font-semibold'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-900">
              <strong>Note:</strong> You can schedule rides up to 7 days in advance. Driver allocation will begin 15 minutes before your scheduled time.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t p-4">
          <Button
            onClick={handleSchedule}
            disabled={!selectedDate || !selectedTime}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold disabled:bg-gray-300"
          >
            {selectedDate && selectedTime
              ? `Schedule for ${new Date(`${selectedDate}T${selectedTime}`).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
              : 'Select Date & Time'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ScheduleRideModal;
